/**
 * Crop frame geometry. A frame is { x, y, w, h } in fractions of the (rotated)
 * photo, so it survives any screen size and is what the server stores.
 */

export const FULL_FRAME = { x: 0, y: 0, w: 1, h: 1 };

// Smallest frame side, as a fraction of the photo: still a usable picture.
export const MIN_SIDE = 0.05;

// Formats offered in the cropper: photo width / height, null = free.
export const FORMATS = [
  { key: 'free', ratio: null },
  { key: 'landscape', ratio: 4 / 3 },
  { key: 'square', ratio: 1 },
];

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/** Width and height once the photo is turned by `rotation` degrees. */
export function rotatedSize(width, height, rotation) {
  return rotation % 180 === 0 ? { width, height } : { width: height, height: width };
}

/**
 * A photo ratio (pixels) in frame units: a 4:3 frame on a 4:3 photo is 1:1
 * in fractions.
 */
export function fractionRatio(ratio, photoWidth, photoHeight) {
  return ratio ? ratio * (photoHeight / photoWidth) : null;
}

export function isFullFrame(frame, rotation = 0) {
  const eps = 0.001;
  return rotation % 360 === 0
    && frame.x < eps && frame.y < eps && frame.w > 1 - eps && frame.h > 1 - eps;
}

/** The frame moved by (dx, dy), kept inside the photo. */
export function moveFrame(frame, dx, dy) {
  return {
    ...frame,
    x: clamp(frame.x + dx, 0, 1 - frame.w),
    y: clamp(frame.y + dy, 0, 1 - frame.h),
  };
}

/**
 * The frame resized by dragging `handle` ('n', 'se', 'w'…) by (dx, dy).
 * With `ratio` (frame units), only corners resize and the format is kept,
 * anchored on the opposite corner.
 */
export function resizeFrame(frame, handle, dx, dy, ratio = null) {
  let left = frame.x;
  let top = frame.y;
  let right = frame.x + frame.w;
  let bottom = frame.y + frame.h;
  if (handle.includes('w')) left = clamp(left + dx, 0, right - MIN_SIDE);
  if (handle.includes('e')) right = clamp(right + dx, left + MIN_SIDE, 1);
  if (handle.includes('n')) top = clamp(top + dy, 0, bottom - MIN_SIDE);
  if (handle.includes('s')) bottom = clamp(bottom + dy, top + MIN_SIDE, 1);

  if (ratio) {
    const fromTop = handle.includes('n');
    const fromLeft = handle.includes('w');
    let w = right - left;
    let h = w / ratio;
    const maxH = fromTop ? bottom : 1 - top;
    if (h > maxH) {
      h = maxH;
      w = h * ratio;
    }
    if (w < MIN_SIDE || h < MIN_SIDE) {
      const grow = Math.max(MIN_SIDE / w, MIN_SIDE / h);
      w *= grow;
      h *= grow;
    }
    if (fromLeft) left = right - w; else right = left + w;
    if (fromTop) top = bottom - h; else bottom = top + h;
  }
  return { x: left, y: top, w: right - left, h: bottom - top };
}

/**
 * The largest frame of `ratio` (frame units) that fits the photo, centred on
 * the current frame as far as the photo allows. No ratio: the frame as is.
 */
export function fitFormat(frame, ratio) {
  if (!ratio) return frame;
  const w = ratio >= 1 ? 1 : ratio;
  const h = ratio >= 1 ? 1 / ratio : 1;
  const cx = frame.x + frame.w / 2;
  const cy = frame.y + frame.h / 2;
  return { x: clamp(cx - w / 2, 0, 1 - w), y: clamp(cy - h / 2, 0, 1 - h), w, h };
}

/** The frame in pixels of a photo of that size, rounded and in bounds. */
export function framePixels(frame, width, height) {
  const sx = Math.round(frame.x * width);
  const sy = Math.round(frame.y * height);
  return {
    sx,
    sy,
    sw: Math.max(1, Math.min(width - sx, Math.round(frame.w * width))),
    sh: Math.max(1, Math.min(height - sy, Math.round(frame.h * height))),
  };
}

/** A saved frame (crop_json) back as { rotation, frame }, or the whole photo. */
export function parseSavedCrop(json) {
  try {
    const c = JSON.parse(json || '');
    const ok = [0, 90, 180, 270].includes(c.rotation)
      && [c.x, c.y, c.w, c.h].every((v) => typeof v === 'number' && v >= 0 && v <= 1)
      && c.w > 0 && c.h > 0;
    if (ok) return { rotation: c.rotation, frame: { x: c.x, y: c.y, w: c.w, h: c.h } };
  } catch {
    // not saved yet, or unreadable: start from the whole photo
  }
  return { rotation: 0, frame: FULL_FRAME };
}
